/**
 * Pure helpers for the per-cell map overlay (walls + doors).
 *
 * Mirrors the PHP-side MapOverlay so client and server agree on what's
 * passable / opaque. Kept DOM-free so it can be unit-tested in node.
 */

import type { MapOverlay, OverlayCell } from './types.js';

/** Canonical key for a cell coordinate. */
export function cellKey(x: number, y: number): string {
  return `${x},${y}`;
}

/** Parse a cell key back into coordinates. Returns null on malformed input. */
export function parseCellKey(key: string): { x: number; y: number } | null {
  const m = key.match(/^(-?\d+),(-?\d+)$/);
  if (m === null) return null;
  return { x: Number(m[1]), y: Number(m[2]) };
}

function isBlockingMovement(overlay: MapOverlay | null, x: number, y: number): boolean {
  if (overlay === null) return false;
  const cell = overlay[cellKey(x, y)];
  if (cell === undefined) return false;
  if (cell.kind === 'solid') return true;
  if (cell.kind === 'door') return cell.open === false;
  if (cell.kind === 'terrain') return true;
  return false;
}

function isBlockingVision(overlay: MapOverlay | null, x: number, y: number): boolean {
  if (overlay === null) return false;
  const cell = overlay[cellKey(x, y)];
  if (cell === undefined) return false;
  if (cell.kind === 'solid') return true;
  if (cell.kind === 'door') return cell.open === false;
  return false;
}

/** A cell blocks movement when it's solid, a closed door, or terrain. */
export function isBlocking(overlay: MapOverlay | null, x: number, y: number): boolean {
  return isBlockingMovement(overlay, x, y);
}

export function isPassable(overlay: MapOverlay | null, x: number, y: number): boolean {
  return !isBlockingMovement(overlay, x, y);
}

/** A cell blocks vision when it's solid or a closed door. Terrain does NOT block vision. */
export function isOpaque(overlay: MapOverlay | null, x: number, y: number): boolean {
  return isBlockingVision(overlay, x, y);
}

/** True if this cell has any overlay entry (solid or door, regardless of door state). */
export function isOverlayCell(overlay: MapOverlay | null, x: number, y: number): boolean {
  if (overlay === null) return false;
  return overlay[cellKey(x, y)] !== undefined;
}

export function getCell(overlay: MapOverlay | null, x: number, y: number): OverlayCell | null {
  if (overlay === null) return null;
  return overlay[cellKey(x, y)] ?? null;
}

/**
 * Apply a paint mode to a cell. Returns a new overlay (immutable).
 *   'paint-solid' → set kind=solid (overwrites doors too)
 *   'erase'       → remove the cell entry entirely
 */
export function applyPaint(
  overlay: MapOverlay | null,
  x: number,
  y: number,
  mode: 'paint-solid' | 'paint-terrain' | 'erase',
): MapOverlay {
  const next: MapOverlay = { ...(overlay ?? {}) };
  const key = cellKey(x, y);
  if (mode === 'paint-solid') {
    next[key] = { kind: 'solid' };
  } else if (mode === 'paint-terrain') {
    next[key] = { kind: 'terrain' };
  } else {
    delete next[key];
  }
  return next;
}

/** Set a specific cell value (used by context-menu actions). null deletes. */
export function setCell(
  overlay: MapOverlay | null,
  x: number,
  y: number,
  cell: OverlayCell | null,
): MapOverlay {
  const next: MapOverlay = { ...(overlay ?? {}) };
  const key = cellKey(x, y);
  if (cell === null) {
    delete next[key];
  } else {
    next[key] = cell;
  }
  return next;
}

/**
 * Decide what paint mode to start when the user mousedowns on a cell.
 * Free cells start a paint-solid stroke; solid/door cells start an erase
 * stroke. Doors-open behave the same as free for paint purposes (since
 * they're passable) but the user probably wants to paint over open doors
 * with walls — so treat any overlay entry as "non-free".
 */
export function paintModeFromCell(overlay: MapOverlay | null, x: number, y: number): 'paint' | 'erase' {
  return isOverlayCell(overlay, x, y) ? 'erase' : 'paint';
}
